// @flow

import _ from 'lodash';
import bemify from 'bemify-js';
import React, { type Node, PureComponent } from 'react';

import { Timeout } from '@zapier/common-components';
import { createKeyHandler } from '@zapier/common-utils';

import Icon from 'app/common/components/Icon';
import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import Popover from 'app/common/components/Popover';
import PopoverMessage from 'app/common/components/PopoverMessage';
import RenameZap from 'app/dashboard/components/RenameZap';
import RunZap from 'app/dashboard/components/RunZap';
import ToggleSwitch from 'app/common/components/ToggleSwitch';
import ZapMenuStateful from 'app/dashboard/components/ZapMenuStateful';
import ZapOwnerStateful from 'app/dashboard/components/ZapOwnerStateful';
import ZapTransferContainer from 'app/dashboard/components/ZapTransfer';
import ZapWarningContainer from 'app/dashboard/components/ZapWarningContainer';
import ZapStepIcons, {
  truncateStepsToMax,
} from 'app/common/components/ZapStepIcons';
import { GUIDED_EDIT_MODE } from 'app/beta-editor/constants/EditModes';
import { homeFolder } from 'app/dashboard/utils/constants';
import { isSharingPrivately } from 'app/shared-zap/SharedZapUtils';
import { isZapInTrash } from 'app/dashboard/utils/DashboardUtils';
import {
  getEditMode,
  isPaused,
  isRemoving,
} from 'app/beta-editor/utils/FlowUtils';
import { getRef, setRef } from 'app/common/utils/ReactUtils';

import type {
  ErrorNodeDetails,
  ZapSummary,
} from '@zapier/design-system-zap-sharing';

import type {
  FolderIdType,
  ZapRunStatusType,
  ZapType,
} from 'app/dashboard/types';
import type { ServicesMap } from 'app/service/types';
import type { UnpauseOriginType } from 'app/beta-editor/types';
import type { ZapStepIconType } from 'app/common/components/ZapStepIcons';

const bem = bemify('dashboard-zap');

type ZapProps = {
  canSharePublicly: boolean,
  owner: {
    avatarUrl: string,
    id: number,
    name: string,
  },
  zap: Object,
  zapSummary: ZapSummary,
  folders: Object,
  services: ServicesMap,
  disabledMessage?: Node,
  zapRunStatus: ZapRunStatusType,
  hasHeldTasks: boolean,
  isOwned: boolean,
  shouldHideMenu: boolean,
  isDisabled?: boolean,
  isDisabledByTeams?: boolean,
  isInTrash?: boolean,
  isInHeldFolder?: boolean,
  isPreview?: boolean,
  isInHeldFolder?: boolean,
  copyZapContext: {
    lastCopiedZapId?: number,
  },
  errorNodeDetails: ErrorNodeDetails,
  runZap: (zapId: number) => void,
  runZapStatus: (zapId: number) => void,
  saveUpdateTitle: (id: number, title: string) => void,
  saveUpdateCustomUser: (id: Number, customuserId: number) => void,
  move: (id: number, folderId: FolderIdType) => void,
  copy: (zap: ZapType) => void,
  remove: (id: number) => void,
  unpause: (id: number, origin: UnpauseOriginType) => void,
  pause: (id: number) => void,
  routeTo: (path: string, objectWithEventInside?: Object) => void,
  onZapDeleted: (zap: ZapType) => void,
  onZapCopied: (zap: ZapType) => void,
  onZapMoved: (zapId: number, folderId: FolderIdType) => void,
  onZapRenamed: (zapId: number, title: string) => void,
  onZapTransfered: (zapId: number, customuserId: number) => void,
  onZapToggled: () => void,
  track: (message: string, data: *) => void,
};

type ZapState = {
  zapMenuVisible: boolean,
  renameModalVisible: boolean,
  runModalVisible: boolean,
  transferModalVisible: boolean,
  zapTooltipTarget: string,
  zapTooltipMessage: string | Node,
};

class ZapStateful extends PureComponent<ZapProps, ZapState> {
  static defaultProps = {
    disabledMessage: 'This Zap is currently disabled.',
    zapRunStatus: [],
  };

  zapContainer: (event: SyntheticKeyboardEvent<HTMLElement>) => void;
  onEditClicked: (event: SyntheticEvent<HTMLElement>) => void;

  constructor(props: ZapProps) {
    super(props);

    this.state = {
      zapMenuVisible: false,
      renameModalVisible: false,
      runModalVisible: false,
      transferModalVisible: false,
      zapTooltipTarget: '',
      zapTooltipMessage: '',
    };
  }

  componentDidMount = () => {
    const {
      copyZapContext: { lastCopiedZapId },
      zap,
    } = this.props;

    const isZapFromACopy = lastCopiedZapId === zap.id;
    if (isZapFromACopy) {
      this.zapContainer = getRef(this, 'zapContainer');
      this.zapContainer.focus();
    }
  };

  // invoked when the user clicks on the "drop-down" arrow button,
  // which tells us that it's time to show/hide the Zap Menu.
  onMenuToggleClicked = (event: SyntheticKeyboardEvent<EventTarget>) => {
    const zapMenuVisible = !this.state.zapMenuVisible;
    this.setState({ zapMenuVisible });
    if (event) {
      event.stopPropagation();
    }
  };

  onIgnoreMenuClick = (event: SyntheticEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  // Hides the Zap Menu, Run Modal, and Rename Modal
  hideEverything = () => {
    this.setState({
      zapMenuVisible: false,
      runModalVisible: false,
      renameModalVisible: false,
      transferModalVisible: false,
      zapTooltipTarget: '',
      zapTooltipMessage: '',
    });
  };

  onRunZapShow = () => {
    this.setState({ zapMenuVisible: false, runModalVisible: true });
  };

  onRenameZapShow = () => {
    this.setState({ zapMenuVisible: false, renameModalVisible: true });
  };

  onRenameZapSave = (title: string) => {
    this.props.saveUpdateTitle(this.props.zap.id, title);
    this.hideEverything();
    if (this.props.onZapRenamed) {
      this.props.onZapRenamed(this.props.zap.id, title);
    }
  };

  onTransferZapShow = () => {
    this.setState({ zapMenuVisible: false, transferModalVisible: true });
  };

  onTransferZapSave = (customuserId: number) => {
    this.props.saveUpdateCustomUser(this.props.zap.id, customuserId);
    this.hideEverything();
    if (this.props.onZapTransfered) {
      this.props.onZapTransfered(this.props.zap.id, customuserId);
    }
  };

  onMoveZap = (folderId: FolderIdType) => {
    this.props.move(this.props.zap.id, folderId);
    this.hideEverything();
    if (this.props.onZapMoved) {
      this.props.onZapMoved(this.props.zap.id, folderId);
    }
  };

  onCopyZap = () => {
    this.props.copy(this.props.zap);
    this.hideEverything();
    if (this.props.onZapCopied) {
      this.props.onZapCopied(this.props.zap);
    }
  };

  onDeleteZap = () => {
    this.props.remove(this.props.zap.id);
    this.hideEverything();
    if (this.props.onZapDeleted) {
      this.props.onZapDeleted(this.props.zap);
    }
  };

  onViewHistory = () => {
    this.props.routeTo(`/app/history?root_id=${this.props.zap.id}`);
  };

  // We want the click targets to cover the entire height of the container, so we just add the handler to the
  // container, and then we check the x position against the rect of the targets.
  onClickContainer = (event: SyntheticKeyboardEvent<HTMLElement>) => {
    // Pass ctrl/meta event through.
    // TODO: Can't get around flow error requiring ctrlKey, metaKey and shiftKey
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey)) {
      this.onEditClicked(event);
      return;
    }

    const menuToggle = getRef(this, 'menuToggle');
    const zapMenu = getRef(this, 'zapMenu');
    const pauseToggle = getRef(this, 'pauseToggle');
    if (menuToggle && menuToggle.contains(event.target)) {
      this.onMenuToggleClicked(event);
    } else if (zapMenu && zapMenu.contains(event.target)) {
      return; // nothing to do here, individual choice items will handle clicks
    } else if (pauseToggle && pauseToggle.contains(event.target)) {
      // do nothing, this is handled by ToggleSwitch's click handler
      return;
    } else {
      this.onEditClicked();
    }
  };

  onEditClicked = (event?: SyntheticEvent<HTMLElement>) => {
    if (this.props.isPreview) {
      return;
    }

    const { zap } = this.props;
    if (isZapInTrash(zap)) {
      this.setState({
        zapTooltipTarget: 'zap',
        zapTooltipMessage:
          'This Zap must be moved out of the Trash folder to edit it.',
      });
      return;
    }
    const editorRoute = `/app/editor/${zap.id}`;
    if (event) {
      this.props.routeTo(editorRoute, { event });
    } else {
      this.props.routeTo(editorRoute);
    }
  };

  onCancelTooltip = () => {
    this.setState({
      zapTooltipTarget: '',
      zapTooltipMessage: '',
    });
  };

  onToggleZap = (isOn: boolean, event: SyntheticEvent<HTMLElement>) => {
    if (this.props.isPreview) {
      return;
    }
    // If this is a draft or in the trash, just pretend like this is a click on the Zap.
    if (this.props.zap.state === 'draft' || this.props.isInTrash) {
      this.onEditClicked();
      return;
    }
    // Prevent click-mashing.
    if (!this.props.zap.isToggling && !this.isDisabled()) {
      if (this.props.onZapToggled) {
        this.props.onZapToggled();
      }
      if (this.props.zap.paused) {
        this.props.unpause(this.props.zap.id, 'dashboard');
      } else {
        this.props.pause(this.props.zap.id);
      }
    }
    // Don't bubble up to the container. Bunch o' mess needing refactoring here.
    event.stopPropagation();
  };

  isDisabled = () => {
    const { isDisabled, isInTrash, isPreview } = this.props;
    return isDisabled || isInTrash || this.isZapDraft() || isPreview;
  };

  onMouseEnterPauseToggle = (event: SyntheticEvent<EventTarget>) => {
    if (this.props.isInTrash) {
      return;
    }

    if (event) {
      event.stopPropagation();
    }

    const isDraftOrNewUser = this.isZapDraft() || this.isNewUser();

    if (
      (this.state.zapTooltipTarget !== 'toggle' && isDraftOrNewUser) ||
      this.props.isDisabled
    ) {
      const zapTooltipMessage = this.isZapDraft()
        ? 'This Zap is not complete'
        : this.props.disabledMessage;
      this.setState({ zapTooltipTarget: 'toggle', zapTooltipMessage });
    }
  };

  onMouseLeavePauseToggle = (event: SyntheticEvent<HTMLElement>) => {
    if (this.props.isInTrash) {
      return;
    }

    if (event) {
      event.stopPropagation();
    }

    const isDraftOrNewUser = this.isZapDraft() || this.isNewUser();

    if (this.state.zapTooltipTarget === 'toggle' && isDraftOrNewUser) {
      this.setState({ zapTooltipTarget: '', zapTooltipMessage: '' });
    }
  };

  onMouseLeaveZap = () => {
    this.setState({ zapTooltipTarget: '', zapTooltipMessage: '' });
  };

  isNewUser = () => {
    const { isOwned, owner } = this.props;
    return !isOwned && !owner;
  };

  // Returns true if the Zap can be run manually
  canRun = () => {
    const { zap } = this.props;
    if (zap.paused) {
      return false;
    }

    const node = zap.nodes[zap.nodeIds[0]];

    if (NodeUtils.isInstant(node, this.props.services)) {
      return false;
    }

    return true;
  };

  isZapInstant = () => {
    const { zap } = this.props;
    const rootNode = zap.nodes[zap.id];
    return NodeUtils.isInstant(rootNode, this.props.services);
  };

  isGuidedMode = () => {
    return getEditMode(this.props.zap) === GUIDED_EDIT_MODE;
  };

  isZapOn = () => {
    return !isPaused(this.props.zap);
  };

  isZapDraft = () => {
    return this.props.zap.state === 'draft';
  };

  ariaZapTitleLabel = (steps: ZapStepIconType[]) => {
    const { zap } = this.props;
    const { title } = zap;
    const hasZapTitle = !!title;
    if (hasZapTitle) {
      return undefined;
    }
    const appTitles = _.compact(
      truncateStepsToMax(3, steps).map(step =>
        _.get(this.props.services, [step.key, 'name'])
      )
    );
    return `Name your Zap for ${appTitles.join(' to ')}`;
  };

  render() {
    const { props } = this;
    const {
      zap,
      copyZapContext: { lastCopiedZapId },
    } = props;

    const title = zap.title || NodeUtils.EMPTY_ZAP_NAME;

    const daClassName = 'zap-mini dashboard not_open';

    let runZapCallback; // leave this undefined to indicate it's not runnable.
    if (this.canRun()) {
      runZapCallback = this.onRunZapShow;
    }

    const folders = [homeFolder, ..._.values(props.folders)];
    const isPrivatelyShared = isSharingPrivately(zap);
    const zapMenu = !this.state.zapMenuVisible ? null : (
      <ZapMenuStateful
        canSharePublicly={props.canSharePublicly}
        folders={folders}
        id={zap.id}
        isFolderShare={zap.is_shared}
        isInHeldFolder={props.isInHeldFolder}
        isInTrash={props.isInTrash}
        isOwner={props.isOwned}
        isPaused={zap.paused}
        isZapShared={isPrivatelyShared}
        onClose={this.hideEverything}
        onConfirmDelete={this.onDeleteZap}
        onConfirmMove={this.onMoveZap}
        onCopy={this.onCopyZap}
        onEdit={this.onEditClicked}
        onRename={this.onRenameZapShow}
        onRun={runZapCallback}
        onTransfer={this.onTransferZapShow}
        onViewHistory={this.onViewHistory}
        ownFolderIds={props.zap.folder_ids}
        ref={setRef(this, 'zapMenu')}
        title={zap.title}
        track={props.track}
        zapSummary={props.zapSummary}
      />
    );

    let zapModal;
    if (this.state.renameModalVisible) {
      zapModal = (
        <RenameZap
          onCloseModal={this.hideEverything}
          onSave={this.onRenameZapSave}
          zapTitle={zap.title}
        />
      );
    } else if (this.state.runModalVisible) {
      zapModal = (
        <RunZap
          onCloseModal={this.hideEverything}
          runZap={props.runZap}
          runZapStatus={props.runZapStatus}
          zapId={props.zap.id}
          zapRunStatus={props.zapRunStatus}
          zapTitle={props.zap.title}
        />
      );
    } else if (this.state.transferModalVisible) {
      zapModal = (
        <ZapTransferContainer
          onCancel={this.hideEverything}
          onTransferZap={this.onTransferZapSave}
          zap={props.zap}
        />
      );
    }

    const toggle = (
      <ToggleSwitch
        isDisabled={this.isDisabled() || props.zap.isToggling}
        isOn={this.isZapOn()}
        isToggling={props.zap.isToggling}
        kind={`small ${props.hasHeldTasks ? 'held' : ''}`}
        noun="zap"
        onToggle={this.onToggleZap}
        title="small toggle"
      />
    );

    const togglePopoverMessage = this.state.zapTooltipTarget === 'toggle' &&
      !!this.state.zapTooltipMessage && (
        <PopoverMessage>{this.state.zapTooltipMessage}</PopoverMessage>
      );

    // TODO: Flow expects zapTooltipMessage to be anything, but isString requires a string value.
    // $FlowFixMe
    const isComplexMessage = !_.isString(this.state.zapTooltipMessage);

    const popoverKind = {
      dark: !props.isDisabledByTeams,
      teams: props.isDisabledByTeams,
      compact: true,
      'no-wrap': !isComplexMessage,
      'text-align-left': isComplexMessage,
    };

    const togglePopover = (
      <Popover kind={popoverKind} shouldAttach={true}>
        {toggle}
        {togglePopoverMessage}
      </Popover>
    );

    const pauseToggle = this.isZapDraft() ? (
      togglePopover
    ) : (
      <Timeout
        delay={2000}
        isWaiting={this.state.zapTooltipTarget === 'toggle'}
        onTimeout={this.onCancelTooltip}
      >
        {togglePopover}
      </Timeout>
    );

    const ownerAvatar = !(
      props.zap.is_shared && _.get(props, ['owner', 'id'])
    ) ? null : (
      <div className="dashboard-zap__owner">
        <ZapOwnerStateful {...props.owner} />
      </div>
    );

    const isZapFromACopy = lastCopiedZapId === zap.id;
    if (isZapFromACopy && this.zapContainer) {
      this.zapContainer.focus();
    }

    const className = bem({
      '--is-removing': isRemoving(zap),
      '--preview': props.isPreview,
      '--copy': isZapFromACopy,
    });

    const steps = ZapStepIcons.stepsFromZap(zap);
    const ariaZapTitleLabel = this.ariaZapTitleLabel(steps);

    const zapPopoverMessage =
      this.state.zapTooltipTarget !== 'zap' ? null : (
        <Timeout
          delay={2000}
          isWaiting={this.state.zapTooltipTarget === 'zap'}
          onTimeout={this.onCancelTooltip}
        >
          <div>{this.state.zapTooltipMessage}</div>
        </Timeout>
      );
    return (
      <Popover kind="dark compact no-wrap" shouldAttach={true} tag="div">
        <div
          className={className}
          onKeyDown={
            this.state.zapMenuVisible
              ? null
              : createKeyHandler(['enter'], () => this.onEditClicked())
          }
          onMouseLeave={this.onMouseLeaveZap}
          ref={setRef(this, 'zapContainer')}
          role="button"
          tabIndex={0}
        >
          <div
            className={daClassName}
            data-zap-id={props.zap.id}
            onClick={this.onClickContainer}
            role="presentation"
          >
            <div className="twrapper dashboard-zap__wrapper">
              <div className="dashboard-zap__logos">
                <ZapStepIcons max={3} steps={ZapStepIcons.stepsFromZap(zap)} />
              </div>
              <div
                aria-label={ariaZapTitleLabel}
                className="title dashboard-zap__title"
                role="button"
                tabIndex={0}
              >
                {this.isGuidedMode() && !props.isPreview ? (
                  <span className="dashboard-zap__label template-zap">
                    template
                  </span>
                ) : null}
                {this.isZapInstant() ? (
                  <span className="dashboard-zap__label instant-zap">
                    instant
                  </span>
                ) : null}
                {this.isZapDraft() && !props.isPreview ? (
                  <span className="dashboard-zap__label draft-zap">draft</span>
                ) : null}
                {isZapFromACopy && (
                  <Icon className={bem('__icon')} size={18} type="copy" />
                )}
                {isPrivatelyShared && (
                  <Icon className={bem('__icon')} size={18} type="team" />
                )}
                <span className={bem('__title-text')}>{title}</span>
              </div>
              {ownerAvatar}
              <div
                className="controls dashboard-zap__controls"
                onMouseLeave={this.onMouseLeavePauseToggle}
                role="presentation"
              >
                <span
                  className="dashboard-toggle"
                  onMouseEnter={this.onMouseEnterPauseToggle}
                  onMouseLeave={this.onMouseLeavePauseToggle}
                  ref={setRef(this, 'pauseToggle')}
                  role="presentation"
                >
                  {pauseToggle}
                </span>
                {props.shouldHideMenu ? null : (
                  <div
                    aria-label="menu"
                    className={
                      this.state.zapMenuVisible ? 'open-menu open' : 'open-menu'
                    }
                    onClick={this.onMenuToggleClicked}
                    onKeyDown={
                      this.state.zapMenuVisible
                        ? null
                        : createKeyHandler(['enter'], this.onMenuToggleClicked)
                    }
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="menu-button"
                      ref={setRef(this, 'menuToggle')}
                    >
                      <Icon type="arrow-down" />
                    </div>
                    {/* This is just here to ignore menu clicks so they don't close the menu. */}
                    <div // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                      onClick={this.onIgnoreMenuClick}
                    >
                      {zapMenu}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <ZapWarningContainer services={props.services} zapId={props.zap.id} />
          {zapModal}
        </div>
        {zapPopoverMessage}
      </Popover>
    );
  }
}

export default ZapStateful;
